import { clsx } from "clsx"
import { forwardRef, useLayoutEffect } from "react"

import { type SX, theme, type ThemeKeys } from "../../theme"
import styles from "./Icon.module.css"
import type { IconName } from "./iconNames"

export interface IconProps {
  className?: string
  color?: ThemeKeys<"color">
  name: IconName
  size?: "extraSmall" | "small" | "medium" | "large"
  sx?: SX
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(
  function Icon(props, ref) {
    const { className, color, name, size, sx, ...otherProps } = props

    const iconPath = `../../icons/${name}.svg`

    useLayoutEffect(() => maybeAddIconTemplate(iconPath))

    return (
      <svg
        {...otherProps}
        className={clsx(styles.icon, size && styles[`size-${size}`], className)}
        style={{ ...sx, color: color && theme.color[color] }}
        fill="currentColor"
        ref={ref}
      >
        <use href={`#${iconPath}`} />
      </svg>
    )
  },
)

const allIcons = import.meta.glob(`../../icons/*.svg`, {
  eager: true,
  query: "?raw",
  import: "default",
})

const addedIcons = new Set<string>()

// We could just add the icons directly to body,
// but it would create a lot of noise in the DOM.
let iconContainer: HTMLElement | undefined

function maybeAddIconTemplate(iconPath: string) {
  if (addedIcons.has(iconPath)) return
  addedIcons.add(iconPath)

  const iconString = allIcons[iconPath]

  // Should never happen.
  if (typeof iconString !== "string") return

  if (!iconContainer) {
    iconContainer = document.createElement("div")
    iconContainer.style.display = "none"
    document.body.appendChild(iconContainer)
  }

  const placeholder = document.createElement("div")
  placeholder.innerHTML = iconString
  const icon = placeholder.firstElementChild

  // Should never happen
  if (!icon) return

  icon.setAttribute("id", iconPath)
  iconContainer.appendChild(icon)
}
